import React, { useState, useEffect } from 'react';
import {
  RaffleTierConfig,
  RaffleTicket,
  WinnerSettlementRecord,
  RaffleTierId,
  UserAccount,
} from '../../types';
import {
  INITIAL_RAFFLE_TIERS,
  INITIAL_4TIER_TICKETS,
  INITIAL_SETTLEMENT_RECORDS,
} from '../../data/raffleTiersData';
import { RaffleTierSelectorBanner } from './raffle/RaffleTierSelectorBanner';
import { BuyRaffleTicketModal } from './raffle/BuyRaffleTicketModal';
import { RafflePrizeCatalogView } from './raffle/RafflePrizeCatalogView';
import { RaffleSetupManager } from './raffle/RaffleSetupManager';
import { LiveDrawChamber } from './raffle/LiveDrawChamber';
import { WinnerValidationModule } from './raffle/WinnerValidationModule';
import { WinnerSettlementModule } from './raffle/WinnerSettlementModule';
import { PublicResultsAndRegistry } from './raffle/PublicResultsAndRegistry';
import { RaffleGovernanceManual } from './raffle/RaffleGovernanceManual';
import { checkRestrictedAreaAccess } from '../../utils/auth';

interface RaffleHubProps {
  currentUser?: UserAccount;
}

const STORAGE_KEYS = {
  TIERS: 'uzairue_raffle_tiers_v2',
  TICKETS: 'uzairue_4tier_raffle_tickets_v2',
  SETTLEMENTS: 'uzairue_winner_settlements_v2',
};

export function getRaffleTickets(): RaffleTicket[] {
  try {
    const raw = localStorage.getItem('uzairue_4tier_raffle_tickets_v2');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch (err) {
    console.error('Failed to load raffle tickets:', err);
  }
  return INITIAL_4TIER_TICKETS;
}

export const RaffleHub: React.FC<RaffleHubProps> = ({ currentUser }) => {
  // Local storage state initialization
  const [tiers, setTiers] = useState<RaffleTierConfig[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.TIERS);
      if (saved) return JSON.parse(saved);
    } catch (e) {
      console.error('Failed to parse raffle tiers from storage:', e);
    }
    return INITIAL_RAFFLE_TIERS;
  });

  const [tickets, setTickets] = useState<RaffleTicket[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.TICKETS);
      if (saved) return JSON.parse(saved);
    } catch (e) {
      console.error('Failed to parse raffle tickets from storage:', e);
    }
    return INITIAL_4TIER_TICKETS;
  });

  const [settlements, setSettlements] = useState<WinnerSettlementRecord[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.SETTLEMENTS);
      if (saved) return JSON.parse(saved);
    } catch (e) {
      console.error('Failed to parse settlements from storage:', e);
    }
    return INITIAL_SETTLEMENT_RECORDS;
  });

  // Active tab inside Raffle Hub
  const [activeTab, setActiveTab] = useState<
    'buy' | 'prizes' | 'setup' | 'draw' | 'verify' | 'settlement' | 'registry' | 'manual'
  >('buy');

  const [selectedTierId, setSelectedTierId] = useState<RaffleTierId>('mega');
  const [isBuyModalOpen, setIsBuyModalOpen] = useState<boolean>(false);
  const [targetBuyTierId, setTargetBuyTierId] = useState<RaffleTierId>('mega');

  // Save changes to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.TIERS, JSON.stringify(tiers));
    } catch (e) {
      console.error('Error saving tiers:', e);
    }
  }, [tiers]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.TICKETS, JSON.stringify(tickets));
    } catch (e) {
      console.error('Error saving tickets:', e);
    }
  }, [tickets]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.SETTLEMENTS, JSON.stringify(settlements));
    } catch (e) {
      console.error('Error saving settlements:', e);
    }
  }, [settlements]);

  // Handle open buy modal
  const handleOpenBuyModal = (tierId: RaffleTierId) => {
    setTargetBuyTierId(tierId);
    setIsBuyModalOpen(true);
  };

  // Handle ticket purchase confirmation
  const handleTicketPurchased = (newTicket: RaffleTicket) => {
    setTickets((prev) => [newTicket, ...prev]);
    alert(
      `🎉 Purchase Successful! Ticket Issued: ${newTicket.ticketNumber}\n` +
      `Secret 4-Digit Verification PIN: ${newTicket.secretVerificationPin}\n` +
      `Bonus Tickets Included: +${newTicket.bonusTicketsCount || 0} Free Mini Tickets`
    );
  };

  // Handle update tiers parameters
  const handleUpdateTiers = (updatedTiers: RaffleTierConfig[]) => {
    setTiers(updatedTiers);
  };

  // Handle draw winner execution
  const handleDrawWinner = (
    tierId: RaffleTierId,
    prizeId: string,
    winnerTicket: RaffleTicket,
    settlementRecord: WinnerSettlementRecord
  ) => {
    // 1. Mark ticket as won
    setTickets((prev) =>
      prev.map((t) =>
        t.id === winnerTicket.id
          ? {
              ...t,
              status: 'won',
              winningPrizeTitle: settlementRecord.prizeTitle,
            }
          : t
      )
    );

    // 2. Increment prize drawn slots
    setTiers((prev) =>
      prev.map((tier) => {
        if (tier.id === tierId) {
          const updatedPrizes = tier.prizes.map((p) => {
            if (p.id === prizeId) {
              return {
                ...p,
                drawnSlotsCount: p.drawnSlotsCount + 1,
                winnerTicketNumbers: [...(p.winnerTicketNumbers || []), winnerTicket.ticketNumber],
              };
            }
            return p;
          });
          return { ...tier, prizes: updatedPrizes };
        }
        return tier;
      })
    );

    // 3. Add to winner settlements
    setSettlements((prev) => [settlementRecord, ...prev]);
  };

  // Handle update settlement record
  const handleUpdateSettlement = (updated: WinnerSettlementRecord) => {
    setSettlements((prev) =>
      prev.map((s) => (s.id === updated.id ? updated : s))
    );
  };

  // Check if current user has manager clearance for Setup and Draw tabs
  const canAccessRaffleManager =
    currentUser &&
    (currentUser.role === 'admin' ||
      checkRestrictedAreaAccess(currentUser, 'raffle-manager').allowed);

  const selectedTier = tiers.find((t) => t.id === targetBuyTierId) || tiers[0];

  return (
    <div className="space-y-8 pb-16">
      {/* Visual Header Banner */}
      <div className="p-6 sm:p-10 bg-slate-950 border-2 border-amber-500/80 rounded-3xl shadow-2xl space-y-6 text-slate-100 relative overflow-hidden">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 relative z-10">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[10px] font-black uppercase tracking-widest px-3 py-1 bg-amber-500/20 text-amber-300 border border-amber-400/40 rounded-full font-mono">
                👑 Production-Grade 4-Tier Raffle Engine
              </span>
              <span className="text-[10px] font-mono text-emerald-400 bg-emerald-950 px-2.5 py-0.5 rounded-full border border-emerald-800 font-bold">
                HMAC SHA-256 Validated
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl font-serif font-black text-amber-200">
              Uzairue Kingdom Erue Festival Raffle Portal
            </h1>

            <p className="text-xs sm:text-sm text-gray-300 max-w-2xl leading-relaxed">
              Four distinct raffle tiers (Mega, Midi, Mini, Starter) with independent ticket pricing, stipulated winning slots, promotional offers, and automated winner settlement.
            </p>
          </div>

          <div className="p-4 bg-slate-900/90 rounded-2xl border border-amber-500/40 text-center space-y-2 shrink-0">
            <span className="text-[10px] uppercase tracking-wider font-bold text-gray-400">
              Total Active Entries Registered
            </span>
            <div className="text-2xl font-black text-amber-300 font-mono">
              {tickets.length} Tickets
            </div>
            <div className="text-[10px] text-emerald-400 font-bold">
              {settlements.length} Prize Slots Drawn & Settled
            </div>
          </div>
        </div>

        {/* Sub-Navigation Tabs */}
        <div className="flex flex-wrap items-center gap-2 pt-4 border-t border-amber-500/30">
          {[
            { id: 'buy', label: '🎟️ Buy Tickets', icon: '🎟️' },
            { id: 'prizes', label: '🎁 4-Tier Prize Catalog', icon: '🎁' },
            { id: 'setup', label: '⚙️ Raffle Setup & Manager', icon: '⚙️', restricted: true },
            { id: 'draw', label: '🎰 Live Draw Chamber', icon: '🎰', restricted: true },
            { id: 'verify', label: '🔍 Winner Validation', icon: '🔍' },
            { id: 'settlement', label: '💰 Winners Settlement Office', icon: '💰' },
            { id: 'registry', label: '📜 Public Gazette & Results', icon: '📜' },
            { id: 'manual', label: '📚 Regulatory Framework', icon: '📚' },
          ].map((tab) => {
            const isRestricted = tab.restricted && !canAccessRaffleManager;
            const isActive = activeTab === tab.id;

            return (
              <button
                key={tab.id}
                onClick={() => {
                  if (tab.restricted && !canAccessRaffleManager) {
                    alert('⛔ Restricted Manager Area: Requires Raffle Manager or Administrator clearance.');
                    return;
                  }
                  setActiveTab(tab.id as any);
                }}
                className={`px-3.5 py-2 rounded-xl text-xs font-extrabold uppercase tracking-wider transition-all cursor-pointer flex items-center gap-1.5 ${
                  isActive
                    ? 'bg-amber-400 text-slate-950 font-black shadow-lg scale-105'
                    : isRestricted
                    ? 'bg-slate-900/60 text-gray-500 border border-slate-800 hover:border-red-500/30'
                    : 'bg-slate-900 text-gray-300 hover:bg-slate-800 border border-slate-800'
                }`}
              >
                <span>{tab.label}</span>
                {tab.restricted && <span className="text-[9px] text-amber-400">🔒</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* TAB 1: BUY TICKETS & TIER SELECTOR */}
      {activeTab === 'buy' && (
        <div className="space-y-8">
          <RaffleTierSelectorBanner
            tiers={tiers}
            activeTierId={selectedTierId}
            onSelectTier={(id) => setSelectedTierId(id)}
            onOpenBuyModal={handleOpenBuyModal}
          />

          <RafflePrizeCatalogView
            tiers={tiers}
            selectedTierId={selectedTierId}
            onSelectTierFilter={(id) => {
              if (id !== 'all') setSelectedTierId(id);
            }}
            onOpenBuyModal={handleOpenBuyModal}
          />
        </div>
      )}

      {/* TAB 2: PRIZE CATALOG VIEW */}
      {activeTab === 'prizes' && (
        <RafflePrizeCatalogView
          tiers={tiers}
          selectedTierId="all"
          onSelectTierFilter={() => {}}
          onOpenBuyModal={handleOpenBuyModal}
        />
      )}

      {/* TAB 3: RESTRICTED SETUP & PARAMETER MANAGER */}
      {activeTab === 'setup' && (
        <RaffleSetupManager
          currentUser={currentUser}
          tiers={tiers}
          onUpdateTiers={handleUpdateTiers}
        />
      )}

      {/* TAB 4: LIVE DRAW CHAMBER */}
      {activeTab === 'draw' && (
        <LiveDrawChamber
          currentUser={currentUser}
          tiers={tiers}
          tickets={tickets}
          onDrawWinner={handleDrawWinner}
        />
      )}

      {/* TAB 5: WINNER VALIDATION MODULE */}
      {activeTab === 'verify' && (
        <WinnerValidationModule
          tickets={tickets}
          settlements={settlements}
          tiers={tiers}
        />
      )}

      {/* TAB 6: WINNERS SETTLEMENT OFFICE */}
      {activeTab === 'settlement' && (
        <WinnerSettlementModule
          currentUser={currentUser}
          settlements={settlements}
          onUpdateSettlement={handleUpdateSettlement}
        />
      )}

      {/* TAB 7: PUBLIC GAZETTE & RESULTS */}
      {activeTab === 'registry' && (
        <PublicResultsAndRegistry
          tickets={tickets}
          settlements={settlements}
          tiers={tiers}
        />
      )}

      {/* TAB 8: GOVERNANCE MANUAL */}
      {activeTab === 'manual' && <RaffleGovernanceManual />}

      {/* Buy Ticket Checkout Modal */}
      <BuyRaffleTicketModal
        tier={selectedTier}
        isOpen={isBuyModalOpen}
        onClose={() => setIsBuyModalOpen(false)}
        onTicketPurchased={handleTicketPurchased}
      />
    </div>
  );
};

export default RaffleHub;
